import { WebLayout } from 'components/Layout';
import { motion } from 'framer-motion';
import HeaderBackgroundImg from 'assets/workshop.png';
import ImpactImage1 from 'assets/community-empowerment.png';
import ImpactImage2 from 'assets/sustainable-craftsmanship.png';
import ImpactImage3 from 'assets/training-mentorship.png';
import woodgrainPattern from 'assets/wood-grain-pattern.png';
import { useImpactData } from '../api/getImpactData';

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8 } },
};

const stagger = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.3, delayChildren: 0.2 },
  },
};

export function Impact() {
  const { data: impactData, isLoading: impactDataLoading } = useImpactData();

  console.log(impactData);

  return (
    <WebLayout>
      {/* ===== Hero Section ===== */}
      <section
        className="relative h-[60vh] flex items-center justify-center bg-center bg-cover text-white"
        style={{ backgroundImage: `url(${impactData?.data?.header?.backgroundImage})` }}
      >
        <div className="absolute inset-0 bg-black/60"></div>
        <motion.div
          className="relative z-10 text-center px-4"
          variants={fadeUp}
          initial="hidden"
          animate="visible"
        >
          <h1 className="text-3xl sm:text-6xl font-semibold mb-3 just-me-again-down-here-regular">
            {impactData?.data?.header?.title}
          </h1>
          <p className="text-sm sm:text-base max-w-xl mx-auto text-gray-200">
            {impactData?.data?.header?.caption}
          </p>
        </motion.div>
      </section>

      {/* ===== Introduction Section ===== */}
      <section className="bg-[#FFF6F2] py-20 px-6 sm:px-12 lg:px-24 text-center">
        <motion.div
          className="max-w-4xl mx-auto"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <h2 className="text-2xl sm:text-3xl font-semibold text-[#AC3F1E] mb-6">
            {impactData?.data?.creation?.title}
          </h2>
          <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
            {impactData?.data?.creation?.caption}
          </p>
        </motion.div>
      </section>

      {/* ===== Impact Highlights ===== */}
      <section className="bg-white py-20 px-6 sm:px-12 lg:px-24">
        <motion.div
          className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-10"
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {/* 1. Community Empowerment */}
          <motion.div className="flex flex-col items-center text-center" variants={fadeUp}>
            <img
              src={impactData?.data?.creation?.columns[0]?.image}
              alt={impactData?.data?.creation?.columns[0]?.title}
              className="w-full h-64 object-cover mb-6 rounded-lg shadow-md"
            />
            <h3 className="text-lg font-semibold text-[#AC3F1E] mb-3">
              {impactData?.data?.creation?.columns[0]?.title}
            </h3>
            <p className="text-sm text-gray-700 leading-relaxed">
              {impactData?.data?.creation?.columns[0]?.text}
            </p>
          </motion.div>

          {/* 2. Sustainable Craftsmanship */}
          <motion.div className="flex flex-col items-center text-center" variants={fadeUp}>
            <img
              src={impactData?.data?.creation?.columns[1]?.image}
              alt={impactData?.data?.creation?.columns[1]?.title}
              className="w-full h-64 object-cover mb-6 rounded-lg shadow-md"
            />
            <h3 className="text-lg font-semibold text-[#AC3F1E] mb-3">
              {impactData?.data?.creation?.columns[2]?.title}
            </h3>
            <p className="text-sm text-gray-700 leading-relaxed">
              {impactData?.data?.creation?.columns[2]?.text}
            </p>
          </motion.div>

          {/* 3. Skills Development */}
          <motion.div className="flex flex-col items-center text-center" variants={fadeUp}>
            <img
              src={impactData?.data?.creation?.columns[2]?.image}
              alt={impactData?.data?.creation?.columns[2]?.title}
              className="w-full h-64 object-cover mb-6 rounded-lg shadow-md"
            />
            <h3 className="text-lg font-semibold text-[#AC3F1E] mb-3">
              {impactData?.data?.creation?.columns[2]?.title}
            </h3>
            <p className="text-sm text-gray-700 leading-relaxed">
              {impactData?.data?.creation?.columns[2]?.text}
            </p>
          </motion.div>
        </motion.div>
      </section>

      {/* ===== Stats Section ===== */}
      <section className="bg-[#AC3F1E] text-white py-20 px-6 sm:px-12 lg:px-24 text-center">
        <motion.div
          className="max-w-6xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10"
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {impactData?.data?.statistics?.map((stat, i) => (
            <motion.div
              key={i}
              variants={fadeUp}
              className="flex flex-col items-center justify-center"
            >
              <h3 className="text-4xl font-semibold mb-2">{stat.number}</h3>
              <p className="text-sm tracking-wide">{stat.title}</p>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* ===== Video or Quote Section ===== */}
      <section className="bg-[#FFF6F2] py-24 px-6 sm:px-12 lg:px-24 text-center">
        <motion.div
          className="max-w-3xl mx-auto"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <blockquote className="italic text-lg sm:text-xl text-gray-800 leading-relaxed mb-8">
            "{impactData?.data?.quote?.quoteText}"
          </blockquote>
          <p className="text-sm text-gray-600 font-medium">— {impactData?.data?.quote?.author}</p>
        </motion.div>
      </section>

      {/* ===== CTA Section ===== */}
      <section className="relative bg-[#AC3F1E] text-white py-20 px-6 sm:px-12 lg:px-24 text-center overflow-hidden">
        <motion.div
          className="relative z-10 max-w-3xl mx-auto"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <h2 className="text-2xl sm:text-3xl font-semibold mb-4">
            {impactData?.data?.partner?.title}
          </h2>
          <p className="text-sm sm:text-base text-gray-100 mb-8">
            {impactData?.data?.partner?.content}
          </p>
          <motion.a
            href={impactData?.data?.partner?.buttonUrl}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="inline-block bg-white text-[#AC3F1E] uppercase tracking-wide px-8 py-3 font-semibold text-sm"
          >
            {impactData?.data?.partner?.buttonLabel}
          </motion.a>
        </motion.div>
        <div
          className="absolute inset-0 opacity-10 bg-cover"
          style={{
            backgroundImage: `url(${impactData?.data?.partner?.backgroundImage})`,
          }}
        ></div>
      </section>
    </WebLayout>
  );
}
